import * as React from 'react';
import { Button } from '@/components/forms/ui/button';
import { MarginIndicator } from '@/features/line-items/margin-indicator';
import { PriceProgressBar } from '@/features/line-items/price-progress-bar';
import { NameField } from './name-field';
import { RootErrors } from './root-errors';
import { RootWarnings } from './root-warnings';
import { SubmitButton } from './submit-button';

type Props = {
  name: string;
  mode: 'create' | 'edit';
  isSubmitting: boolean;
  submitDisabled: boolean;
  allocatedPrice: number;
  totalPrice: number;
  plannedMargin: number | null;
  targetMargin: number | null;
  rootErrors: string[];
  rootWarnings: string[];
  hasErrors: boolean;
  submitAttempted: boolean;
  nameError: string | undefined;
  onNameChange: (value: string) => void;
  onNameBlur: () => void;
  onCancel: () => void;
  typeSelector?: React.ReactNode;
};

const GENERIC_SUBMIT_ERROR = "Couldn't save — please fix the highlighted fields below";

export function NameSection({
  name,
  mode,
  isSubmitting,
  submitDisabled,
  allocatedPrice,
  totalPrice,
  plannedMargin,
  targetMargin,
  rootErrors,
  rootWarnings,
  hasErrors,
  submitAttempted,
  nameError,
  onNameChange,
  onNameBlur,
  onCancel,
  typeSelector
}: Props) {
  const showGeneric = submitAttempted && hasErrors && rootErrors.length === 0;
  const showSubmitError = submitAttempted && hasErrors;
  return (
    <div className="sticky top-0 z-100 -mx-6 border-b border-gray-200 bg-white p-6">
      <div className="flex items-center gap-6 pb-4">
        <PriceProgressBar
          allocated={allocatedPrice}
          total={totalPrice}
          hasMismatch={showSubmitError}
        />
        <MarginIndicator planned={plannedMargin} target={targetMargin} />
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex items-start gap-2">
          <NameField
            id="line-item-name"
            value={name}
            onChange={onNameChange}
            onBlur={onNameBlur}
            error={nameError}
          />
          {typeSelector}
          <Button
            type="button"
            variant="blueOutline"
            onClick={onCancel}
            data-testid="line-item-cancel-button">
            Cancel
          </Button>
          <SubmitButton
            mode={mode}
            isSubmitting={isSubmitting}
            disabled={submitDisabled}
            hasErrors={showSubmitError}
          />
        </div>
        <RootErrors errors={rootErrors} />
        <RootWarnings warnings={rootWarnings} />
        {showGeneric ? (
          <div
            className="text-destructive flex justify-end text-sm"
            data-testid="line-item-submit-error">
            {GENERIC_SUBMIT_ERROR}
          </div>
        ) : null}
      </div>
    </div>
  );
}
